import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  Typography,
  Box,
  Grid,
  Paper,
  CircularProgress,
  IconButton,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Tooltip,
  Chip
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PaymentIcon from '@mui/icons-material/Payment';
import AddDebtModal from './AddDebtModal';
import { getAccruals } from '../../../services/migration/personnelTransactionService';
import GuideButton from '../../common/guide/GuideButton';

// Tab paneli için interface
interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

// Tab Paneli bileşeni
function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`modal-tabpanel-${index}`}
      aria-labelledby={`modal-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 2 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

// Tab için yardımcı fonksiyon
function a11yProps(index: number) {
  return {
    id: `modal-tab-${index}`,
    'aria-controls': `modal-tabpanel-${index}`,
  };
}

// Modal prop'ları
interface PaymentModalProps {
  open: boolean;
  onClose: () => void;
  personnelId: string;
  personnelName: string;
  companyId: string | null;
  initialTab?: 'salary' | 'bonus'; // Başlangıçta hangi sekmenin açık olacağını belirten prop
  onSuccess?: () => void;
  /** Şube yöneticisi: ödeme yalnız bu şubenin kasa/banka hesaplarından yapılır. */
  restrictToBranchId?: string;
  hideProject?: boolean;
}

// Personel işlem verisi interface
interface PersonnelTransaction {
  id: string;
  personnelId: string;
  personnelName: string;
  companyId: string | null;
  amount: number;
  description: string;
  paymentMethod: string;
  currency?: string;
  transactionType: string;
  type?: 'SALARY' | 'BONUS'; // Maaş mı, prim mi?
  performedBy: string;
  createdAt: string;
  isAccrual: boolean;
  isPaid?: boolean; // Ödendi mi?
  paidAt?: string; // Ödeme tarihi
  paidBy?: string; // Ödeyen kişi
  paidAmount?: number; // Ödenen miktar
  remainingAmount?: number; // Kalan (kısmi ödemede)
}

const PaymentModal: React.FC<PaymentModalProps> = ({
  open,
  onClose,
  personnelId,
  personnelName,
  companyId,
  initialTab = 'salary',
  onSuccess,
  restrictToBranchId,
  hideProject
}) => {
  
  // Tab state'i - initialTab'e göre başlangıç değeri
  const [tabValue, setTabValue] = useState(initialTab === 'bonus' ? 1 : 0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Tablodaki veriler
  const [salaryRecords, setSalaryRecords] = useState<PersonnelTransaction[]>([]);
  const [bonusRecords, setBonusRecords] = useState<PersonnelTransaction[]>([]);
  
  // Sayfalama için state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  
  // Ödeme modalı için state
  const [openPaymentModal, setOpenPaymentModal] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState<PersonnelTransaction | null>(null);

  // Tab değişikliği
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    setPage(0); // Tab değiştiğinde ilk sayfaya dön
  };

  // Sayfalama değişiklikleri
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Para birimini formatla
  const formatCurrency = (num: number, currencyCode: string = 'TRY') => {
    return new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency: currencyCode,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(num);
  };

  // Tarihi formatla
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('tr-TR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    }) + ' ' + date.toLocaleTimeString('tr-TR', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Personel hakedişlerini çek (SQL — gts_accounting_api PersonnelTransaction accruals)
  const fetchPersonnelTransactions = async () => {
    if (!personnelId || !companyId) return;

    setLoading(true);
    try {
      // unpaidOnly=false: odenmis olanlar da listede (durum chip'i icin) gosterilir.
      // Sadece RECEIVABLE hakedisler listelenir; avans mahsubu gibi accrual-yontemli
      // COLLECTION kayitlari hakedis DEGILDIR ve bu listeye girmemelidir.
      const accruals = (await getAccruals(personnelId, undefined, false))
        .filter((a) => a.transactionType === 'RECEIVABLE');

      const salaryList: PersonnelTransaction[] = [];
      const bonusList: PersonnelTransaction[] = [];

      accruals.forEach((a) => {
        const record: PersonnelTransaction = {
          id: a.id,
          personnelId: a.personnelId,
          personnelName: a.personnelName,
          companyId,
          amount: a.amount,
          description: a.description,
          paymentMethod: a.paymentMethod,
          currency: a.currency,
          transactionType: a.transactionType,
          type: a.kind === 'BONUS' ? 'BONUS' : 'SALARY',
          performedBy: a.performedBy,
          createdAt: a.createdAt,
          isAccrual: a.isAccrual,
          isPaid: a.isPaid,
          paidAt: a.paidAt || undefined,
          paidAmount: a.paidAmount ?? 0,
          remainingAmount: a.remainingAmount ?? (a.isPaid ? 0 : a.amount),
        };
        if (a.kind === 'BONUS') {
          bonusList.push(record);
        } else {
          salaryList.push(record);
        }
      });

      salaryList.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
      bonusList.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

      setSalaryRecords(salaryList);
      setBonusRecords(bonusList);
    } catch (error) {
      console.error("Personel işlemleri alınırken hata:", error);
      setError("İşlemler yüklenirken bir hata oluştu.");
    } finally {
      setLoading(false);
    }
  };

  // Ödeme yap butonuna tıklandığında
  const handleMakePayment = (transaction: PersonnelTransaction) => {
    setSelectedTransaction(transaction);
    setOpenPaymentModal(true);
  };

  // Ödeme modalı kapandığında
  const handlePaymentModalClose = () => {
    setOpenPaymentModal(false);
    setSelectedTransaction(null);
  };

  // Ödeme başarılı olduğunda: ödeme hakedişe bağlı kaydedildi; hakedişi backend, bağlı ödemelerin
  // toplamı tutarı karşılayınca "ödendi" yapar (kısmi ödemede açık kalır, ödeme silinince geri açılır).
  const handlePaymentSuccess = () => {
    fetchPersonnelTransactions();
    if (onSuccess) onSuccess();
  };

  const renderStatus = (record: PersonnelTransaction) => {
    if (record.isPaid) {
      return (
        <Chip
          label={`Ödendi${record.paidAt ? `: ${formatDate(record.paidAt)}` : ''}`}
          color="success"
          size="small"
        />
      );
    }
    if ((record.paidAmount ?? 0) > 0) {
      return (
        <Tooltip title={`Ödenen: ${formatCurrency(record.paidAmount ?? 0, record.currency || 'TRY')}`}>
          <Chip
            label={`Kısmi — kalan ${formatCurrency(record.remainingAmount ?? 0, record.currency || 'TRY')}`}
            color="info"
            size="small"
          />
        </Tooltip>
      );
    }
    return <Chip label="Ödenmedi" color="warning" size="small" />;
  };

  // Modal açıldığında işlemleri yükle
  useEffect(() => {
    if (open) {
      setTabValue(initialTab === 'bonus' ? 1 : 0);
      fetchPersonnelTransactions();
    }
  }, [open, personnelId, companyId, initialTab]);

  return (
    <>
      <Dialog
        open={open}
        onClose={loading ? undefined : onClose}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">
              {tabValue === 0 ? 'Maaş Ödemesi Yap' : 'Prim Ödemesi Yap'}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <GuideButton guideKey="personnel.salaryPayment" color="inherit" disabled={loading} />
              <IconButton edge="end" color="inherit" onClick={onClose} disabled={loading}>
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>
        </DialogTitle>
        
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tabValue} onChange={handleTabChange} aria-label="modal tabs" centered>
            <Tab label="Maaş Ödemeleri" {...a11yProps(0)} />
            <Tab label="Prim Ödemeleri" {...a11yProps(1)} />
          </Tabs>
        </Box>
        
        <DialogContent dividers>
          {error && (
            <Box sx={{ mb: 2, p: 1, bgcolor: 'error.light', color: 'error.contrastText', borderRadius: 1 }}>
              <Typography variant="body2">{error}</Typography>
            </Box>
          )}
          
          <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
            <Typography variant="subtitle1" gutterBottom>
              Personel Bilgisi
            </Typography>
            <Typography variant="h6">{personnelName}</Typography>
            <Typography variant="body2" color="text.secondary">
              ID: {personnelId}
            </Typography>
          </Paper>
          
          <TabPanel value={tabValue} index={0}>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Tarih</TableCell>
                    <TableCell>Açıklama</TableCell>
                    <TableCell align="right">Tutar</TableCell>
                    <TableCell>Durumu</TableCell>
                    <TableCell>İşlem</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                        <CircularProgress size={24} />
                      </TableCell>
                    </TableRow>
                  ) : salaryRecords.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                        <Typography variant="body2" color="text.secondary">
                          Maaş kaydı bulunamadı.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    salaryRecords
                      .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                      .map((record) => (
                        <TableRow key={record.id} hover>
                          <TableCell>{formatDate(record.createdAt)}</TableCell>
                          <TableCell>{record.description}</TableCell>
                          <TableCell align="right">
                            {formatCurrency(record.amount, record.currency || 'TRY')}
                          </TableCell>
                          <TableCell>{renderStatus(record)}</TableCell>
                          <TableCell>
                            {!record.isPaid && (
                              <Tooltip title="Ödeme Yap">
                                <Button 
                                  color="primary" 
                                  size="small"
                                  variant="outlined"
                                  onClick={() => handleMakePayment(record)}
                                >
                                  Ödeme Yap
                                </Button>
                              </Tooltip>
                            )}
                          </TableCell>
                        </TableRow>
                      ))
                  )}
                </TableBody>
              </Table>
              <TablePagination
                rowsPerPageOptions={[5, 10, 25]}
                component="div"
                count={salaryRecords.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                labelRowsPerPage="Sayfa başına satır:"
                labelDisplayedRows={({ from, to, count }) => 
                  `${count} kayıttan ${from}-${to} arası gösteriliyor`
                }
              />
            </TableContainer>
          </TabPanel>
          
          <TabPanel value={tabValue} index={1}>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Tarih</TableCell>
                    <TableCell>Açıklama</TableCell>
                    <TableCell align="right">Tutar</TableCell>
                    <TableCell>Durumu</TableCell>
                    <TableCell>İşlem</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                        <CircularProgress size={24} />
                      </TableCell>
                    </TableRow>
                  ) : bonusRecords.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                        <Typography variant="body2" color="text.secondary">
                          Prim kaydı bulunamadı.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    bonusRecords
                      .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                      .map((record) => (
                        <TableRow key={record.id} hover>
                          <TableCell>{formatDate(record.createdAt)}</TableCell>
                          <TableCell>{record.description}</TableCell>
                          <TableCell align="right">
                            {formatCurrency(record.amount, record.currency || 'TRY')}
                          </TableCell>
                          <TableCell>{renderStatus(record)}</TableCell>
                          <TableCell>
                            {!record.isPaid && (
                              <Tooltip title="Ödeme Yap">
                                <Button 
                                  color="primary" 
                                  size="small"
                                  variant="outlined"
                                  onClick={() => handleMakePayment(record)}
                                >
                                  Ödeme Yap
                                </Button>
                              </Tooltip>
                            )}
                          </TableCell>
                        </TableRow>
                      ))
                  )}
                </TableBody>
              </Table>
              <TablePagination
                rowsPerPageOptions={[5, 10, 25]}
                component="div"
                count={bonusRecords.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                labelRowsPerPage="Sayfa başına satır:"
                labelDisplayedRows={({ from, to, count }) => 
                  `${count} kayıttan ${from}-${to} arası gösteriliyor`
                }
              />
            </TableContainer>
          </TabPanel>
        </DialogContent>
        
        <DialogActions sx={{ p: 2, justifyContent: 'flex-end' }}>
          <Button
            onClick={onClose}
            disabled={loading}
            variant="outlined"
          >
            Kapat
          </Button>
        </DialogActions>
      </Dialog>

      {/* Ödeme yapma modalı */}
      {selectedTransaction && (
        <AddDebtModal
          open={openPaymentModal}
          onClose={handlePaymentModalClose}
          personnelId={personnelId}
          personnelName={personnelName}
          companyId={companyId}
          sourceAction="payment_collection"
          onSuccess={() => {
            handlePaymentSuccess();
            handlePaymentModalClose();
          }}
          initialPaymentDescription={`${personnelName} için ${tabValue === 0 ? 'maaş' : 'prim'} ödemesi`}
          initialPaymentAmount={String(selectedTransaction.remainingAmount ?? selectedTransaction.amount)}
          accrualId={selectedTransaction.id}
          accrualCurrency={selectedTransaction.currency || 'TRY'}
          accrualRemaining={selectedTransaction.remainingAmount ?? selectedTransaction.amount}
          restrictToBranchId={restrictToBranchId}
          hideProject={hideProject}
        />
      )}
    </>
  );
};

export default PaymentModal; 